import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, MailCheck, KeyRound } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      // Backend deliberately returns a generic success. Any error here is a
      // real failure (rate-limit / network) — show it.
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Could not process request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6"
         style={{ background: "var(--miro-bg, #F5F7FB)" }}>
      <div className="max-w-md w-full bg-white border border-[var(--miro-border)] rounded-2xl p-8">
        <Link to="/login" data-testid="forgot-back-link"
              className="inline-flex items-center gap-1 text-xs text-[var(--miro-text-muted)] hover:text-[var(--miro-navy)]">
          <ArrowLeft size={13}/> Back to sign in
        </Link>
        {sent ? (
          <div className="text-center py-4">
            <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center"
                 style={{ background: "var(--miro-navy)" }}>
              <MailCheck className="text-white" size={26}/>
            </div>
            <h1 className="font-display font-black text-2xl text-[var(--miro-navy)]">Check your inbox</h1>
            <p className="text-sm text-[var(--miro-text-secondary)] mt-3">
              If an account matches <strong className="text-[var(--miro-navy)]">{email}</strong>,
              we've sent a password-reset link that expires in <strong>30 minutes</strong>.
            </p>
            <p className="text-xs text-[var(--miro-text-muted)] mt-4">
              Didn't get it? Check your spam folder, or contact your MIRO administrator.
            </p>
            <Button
              data-testid="forgot-back-btn"
              onClick={() => { setSent(false); setEmail(""); }}
              variant="outline"
              className="mt-6 w-full h-11 font-semibold"
            >
              Send another link
            </Button>
          </div>
        ) : (
          <>
            <div className="miro-overline mt-4">Password recovery</div>
            <h1 className="font-display font-black text-3xl tracking-tight text-[var(--miro-navy)]">
              Forgot your password?
            </h1>
            <p className="text-sm text-[var(--miro-text-secondary)] mt-2">
              Enter your work email and we'll send you a secure link to set a new
              password. Links expire after 30 minutes.
            </p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label>Work email</Label>
                <Input
                  data-testid="forgot-email-input"
                  required type="email" className="mt-1"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <Button
                data-testid="forgot-submit-btn"
                type="submit"
                disabled={submitting || !email}
                className="w-full h-11 font-semibold"
                style={{ background: "var(--miro-navy)" }}
              >
                {submitting ? "Sending…" : (<><KeyRound size={15} className="mr-1.5"/> Send reset link</>)}
              </Button>
            </form>
            <div className="mt-6 text-[11px] text-[var(--miro-text-muted)] leading-relaxed">
              We never reveal whether an email is registered — this protects your
              team from account discovery attacks.
            </div>
          </>
        )}
      </div>
    </div>
  );
}
